import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { QueryHandlerResponse } from '../../shared/models/responseModels/QueryHandlerResponse';
import { GetAutomationReportInfoModel } from '../../shared/models/responseModels/GetAutomationReportInfoModel';
import { Observable, Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { ActivatedRoute, Router } from '@angular/router';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import { is } from 'date-fns/locale';
import { AutomationReportMenuItem } from '../model/automation-report-menu-item';
import { formatDate } from '@angular/common';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';

@Component({
    selector: 'app-automation-report',
    templateUrl: './app-automation-report.component.html',
    styleUrls: ['./app-automation-report.component.scss'],
    standalone: false
})
export class AppAutomationReportComponent implements OnInit, OnDestroy {
    private readonly urlResolver = inject(UrlResolverService);
    isMenuVisible = true;
    isLoading = false;
    currentContent = '';
    error = '';
    isAuditCenter = false;
    isAsending = false;
    private readonly pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    private readonly getAutomationReportInfo: string = `${this.pcxQueryUrl}GetAutomationReportInfo` as const;
    urlVersion: string = '';
    urlPortal: string = '';
    currentPortal: string;
    loadingPercentage = 0;
    private loadingInterval: any;
    defaultMenu: AutomationReportMenuItem = {
        id: 'blank',
        title: 'Test Reports 6',
        icon: '⚪',
        url: 'data:text/html;charset=utf-8,' + encodeURIComponent(this.getDefaultContent()),
        createdate: undefined,
        createdby: '',
        version: '',
        reportFileName: '',
        portal: ''
    };
    menuItems: AutomationReportMenuItem[] = [];
    groupedMenuItems: Map<string, AutomationReportMenuItem[]> = new Map();
    monthOrder: string[] = [];

    currentUrl: SafeResourceUrl;
    currentTitle = 'Test Reports';
    isMenuLoading = false;
    private destroy$: Subject<boolean> = new Subject<boolean>();
    private loadingTimeout: any;
    private readonly LOADING_TIMEOUT = 30000;
    retryCount = 0;
    maxRetries = 3;
    constructor(
        private sanitizer: DomSanitizer,
        private httpClient: HttpClient,
        private activateRoute: ActivatedRoute,
        private router: Router
    ) {
        this.currentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.defaultMenu.url);
    }
    ngOnInit(): void {
        this.isLoading = true;
        this.urlVersion = this.activateRoute.snapshot.queryParamMap.get('version') ?? '';
        this.isAuditCenter = true;
        this.currentPortal = PCXConstants.ADUDIT_CENTER;
        this.fetchAutomationReportList();
    }
    ngOnDestroy(): void {
        if (this.loadingInterval) {
            clearInterval(this.loadingInterval);
        }
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
        }
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    loadDefaultContent(): void {
        this.loadContent(this.defaultMenu);
        this.currentContent = '';
        this.resetUrlQueryParams();
    }
    loadContent(menuItem: AutomationReportMenuItem): void {
        if (!menuItem.url) {
            this.error = 'Invalid report URL. Please try another report.';
            return;
        }
        this.isLoading = true;
        this.error = '';
        this.loadingPercentage = 0;
        this.retryCount = 0;
        this.currentContent = menuItem.id;
        this.currentTitle = menuItem.title;
        this.currentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(menuItem.url);
        this.updateUrl(menuItem);
        this.startLoadingProgress();
        this.setLoadingTimeout();
    }

    private updateUrl(menuItem: AutomationReportMenuItem): void {
        const queryParams = {
            portal: this.isAuditCenter ? PCXConstants.ADUDIT_CENTER : PCXConstants.CASE_CENTER,
            version: menuItem.version || null
        };

        this.router.navigate([], {
            relativeTo: this.activateRoute,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    refreshIframe(): void {
        this.isLoading = true;
        this.error = '';
        this.loadingPercentage = 0;
        this.retryCount++;
        const currentMenuItem = this.menuItems.find(item => item.id === this.currentContent);
        if (currentMenuItem) {
            this.currentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(currentMenuItem.url);
        }
        this.startLoadingProgress();
        this.setLoadingTimeout();
    }

    openInNewTab(): void {
        const currentMenuItem = this.menuItems.find(item => item.id === this.currentContent);
        if (currentMenuItem) {
            window.open(currentMenuItem.url, '_blank');
        }
    }

    openBaseUrl(): void {
        const baseUrl = window.location.origin;
        window.open(baseUrl, '_blank');
    }

    sort(): void {
        this.isAsending = !this.isAsending;
        this.groupAndSortItems();
    }

    private groupAndSortItems(): void {
        this.groupedMenuItems.clear();
        this.monthOrder = [];

        const itemsToGroup = this.isAsending ? [...this.menuItems].reverse() : this.menuItems;

        itemsToGroup.forEach(item => {
            const monthKey = this.getMonthKey(item.createdate);
            if (!this.groupedMenuItems.has(monthKey)) {
                this.groupedMenuItems.set(monthKey, []);
                this.monthOrder.push(monthKey);
            }
            this.groupedMenuItems.get(monthKey)!.push(item);
        });
    }

    private getMonthKey(date: any): string {
        if (!date) return 'NO DATE';
        const parsedDate = new Date(date);
        return formatDate(parsedDate, 'MMMM yyyy', 'en-US').toUpperCase();
    }

    getGroupedItems(): Array<{ month: string; items: AutomationReportMenuItem[] }> {
        return this.monthOrder.map(month => ({
            month,
            items: this.groupedMenuItems.get(month) || []
        }));
    }

    getItemStatus(item: AutomationReportMenuItem): string {
        if (!item.createdate) return '';
        const itemDate = new Date(item.createdate);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - itemDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysDiff <= 7 ? '' : '';
    }

    isLatestItem(item: AutomationReportMenuItem): boolean {
        return this.menuItems.length > 0 && this.menuItems[0].id === item.id;
    }
    toggleFullscreen(): void {
        const iframe = document.getElementById('contentFrame') as HTMLIFrameElement;
        if (iframe) {
            if (iframe.requestFullscreen) {
                iframe.requestFullscreen();
            } else if ((iframe as any).webkitRequestFullscreen) {
                (iframe as any).webkitRequestFullscreen();
            } else if ((iframe as any).msRequestFullscreen) {
                (iframe as any).msRequestFullscreen();
            }
        }
    }


    private resetUrlQueryParams() {
        this.router.navigate([], {
            relativeTo: this.activateRoute,
            queryParams: {},
            replaceUrl: true
        });
    }

    private fetchAutomationReportList() {
        this.menuItems = [];
        this.isMenuLoading = true;
        this.getAutomationReportInfoModel(this.currentPortal)
            .pipe(takeUntil(this.destroy$))
            .subscribe(res => {
                if (res && res.Results && (res.TotalCount ?? 0) > 0) {
                    console.log('Automation Report Info:', res.Results);
                    this.menuItems = this.mapResultsToMenuItems(res.Results);
                    this.groupAndSortItems();
                    if (this.urlVersion != null && this.urlVersion != '') {
                        const matchedItem = this.menuItems.find(item => item.version.trim() === this.urlVersion);
                        this.loadContent(matchedItem ?? this.defaultMenu);
                    }
                } else {
                    this.menuItems = [];
                    this.loadDefaultContent();
                }
                this.isMenuLoading = false;
            });
    }

    private mapResultsToMenuItems(results: any): AutomationReportMenuItem[] {
        return results.map((item, index) => ({
            id: item.ItemId,
            title: item.Version || `Report ${index + 1}`,
            icon: '⚪',
            url: this.generateReportUrl(item),
            createdate: item.CreateDate ?? undefined,
            createdby: item.CreatedBy ?? '',
            version: item.Version ?? '',
            reportFileName: item.ReportFileName ?? '',
            portal: item.Portal ?? ''
        }));
    }

    private generateReportUrl(reportInfo: GetAutomationReportInfoModel): string {
        if (reportInfo.ReportFileName) {
            const baseUrl = PCXConstants.AUTOMATION_TEST_REPORT_BASE_URL;
            return `${baseUrl}${reportInfo.ReportFileName}`;
        }

        // Fallback to default content
        return 'data:text/html;charset=utf-8,' + encodeURIComponent(this.getDefaultContent());
    }

    onIframeLoad(): void {
        this.clearLoadingTimeout();
        this.isLoading = false;
        this.loadingPercentage = 100;
        this.error = '';
        if (this.loadingInterval) {
            clearInterval(this.loadingInterval);
            this.loadingInterval = null;
        }
    }

    onIframeError(): void {
        this.handleLoadingError('Failed to load report. The file may be unavailable.');
    }

    private handleLoadingError(errorMessage: string): void {
        this.clearLoadingTimeout();
        this.isLoading = false;
        this.loadingPercentage = 0;
        this.error = errorMessage;
        if (this.loadingInterval) {
            clearInterval(this.loadingInterval);
            this.loadingInterval = null;
        }
    }

    private setLoadingTimeout(): void {
        this.clearLoadingTimeout();
        this.loadingTimeout = setTimeout(() => {
            if (this.isLoading) {
                this.handleLoadingError('Loading timeout. The report took too long to load. Please try again or select another report.');
            }
        }, this.LOADING_TIMEOUT);
    }

    private clearLoadingTimeout(): void {
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
        }
    }

    public getAutomationReportInfoModel(
        portal: string
    ): Observable<QueryHandlerResponse<null, GetAutomationReportInfoModel>> {
        const payload = {
            Portal: portal,
            Descending: true
        };
        return this.httpClient.post<QueryHandlerResponse<null, GetAutomationReportInfoModel>>(
            this.getAutomationReportInfo,
            payload,
            {
                headers: SCConfig.HttpCommonHeader,
                withCredentials: true
            }
        );
    }

    private startLoadingProgress(): void {
        if (this.loadingInterval) {
            clearInterval(this.loadingInterval);
        }
        this.loadingPercentage = 0;
        this.loadingInterval = setInterval(() => {
            if (this.loadingPercentage < 95) {
                const increment = Math.floor(Math.random() * 5) + 1;
                this.loadingPercentage = Math.min(this.loadingPercentage + increment, 95);
            }
        }, 700);
    }
    getDefaultContent(): string {
        const defaultHtmlContent = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Automation Reports Dashboard</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    
                    body {
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        background: #001e3f;
                        color: white;
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        overflow: hidden;
                    }
                    
                    .dashboard-container {
                        width: 100%;
                        height: 100vh;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #001e3f 0%, #002a5c 50%, #001e3f 100%);
                        position: relative;
                    }
                    
                    .dashboard-container::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: 
                            radial-gradient(circle at 20% 80%, rgba(0, 167, 128, 0.1) 0%, transparent 50%),
                            radial-gradient(circle at 80% 20%, rgba(0, 123, 255, 0.1) 0%, transparent 50%);
                        pointer-events: none;
                    }
                    
                    .content-wrapper {
                        text-align: center;
                        background: rgba(255, 255, 255, 0.05);
                        padding: 60px 40px;
                        border-radius: 20px;
                        backdrop-filter: blur(15px);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        box-shadow: 
                            0 8px 32px rgba(0, 0, 0, 0.3),
                            inset 0 1px 0 rgba(255, 255, 255, 0.1);
                        position: relative;
                        z-index: 1;
                        max-width: 500px;
                        width: 90%;
                    }
                    
                    .logo-section {
                        margin-bottom: 30px;
                    }
                    
                    .logo-icon {
                        font-size: 4rem;
                        margin-bottom: 15px;
                        display: block;
                        filter: drop-shadow(0 4px 8px rgba(0, 167, 128, 0.3));
                    }
                    
                    .main-title {
                        font-size: 2.2rem;
                        font-weight: 700;
                        margin-bottom: 15px;
                        background: linear-gradient(135deg, #00a780 0%, #00d4aa 100%);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                    }
                    
                    .subtitle {
                        font-size: 1.1rem;
                        color: rgba(255, 255, 255, 0.85);
                        margin-bottom: 35px;
                        font-weight: 300;
                        line-height: 1.6;
                    }
                    
                    .action-buttons {
                        display: flex;
                        gap: 15px;
                        justify-content: center;
                        flex-wrap: wrap;
                    }
                    
                    .btn {
                        background: linear-gradient(135deg, #00a780 0%, #00d4aa 100%);
                        color: white;
                        padding: 15px 30px;
                        border: none;
                        border-radius: 25px;
                        font-size: 1rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        text-decoration: none;
                        display: inline-block;
                        box-shadow: 0 4px 15px rgba(0, 167, 128, 0.3);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                    }
                    
                    .btn:hover {
                        background: linear-gradient(135deg, #008a6b 0%, #00a780 100%);
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px rgba(0, 167, 128, 0.4);
                    }
                    
                    .status-indicator {
                        position: absolute;
                        top: 20px;
                        right: 20px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background: rgba(0, 167, 128, 0.1);
                        padding: 8px 15px;
                        border-radius: 20px;
                        border: 1px solid rgba(0, 167, 128, 0.3);
                    }
                    
                    .status-dot {
                        width: 8px;
                        height: 8px;
                        background: #00a780;
                        border-radius: 50%;
                        animation: pulse 2s infinite;
                    }
                    
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                    
                    .status-text {
                        font-size: 0.9rem;
                        color: #00a780;
                        font-weight: 500;
                    }
                    
                    .footer-info {
                        margin-top: 25px;
                        padding-top: 20px;
                        border-top: 1px solid rgba(255, 255, 255, 0.1);
                        color: rgba(255, 255, 255, 0.6);
                        font-size: 0.9rem;
                    }
                    
                    @media (max-width: 768px) {
                        .content-wrapper {
                            padding: 40px 20px;
                            margin: 20px;
                        }
                        
                        .main-title {
                            font-size: 1.9rem;
                        }
                        
                        .action-buttons {
                            flex-direction: column;
                            align-items: center;
                        }
                        
                        .btn {
                            width: 100%;
                            max-width: 250px;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="dashboard-container">
                    <div class="content-wrapper">
                        <div class="logo-section">
                            <span class="logo-icon">📊</span>
                            <h1 class="main-title">Automation Reports</h1>
                            <p class="subtitle">Welcome to the Ipex Automation Reports Dashboard. Select a report from the menu to begin your analysis and view detailed test results.</p>
                        </div>
                    </div>
                </div>
            </body>
            </html>`;
        return defaultHtmlContent;
    }
}
